import { Component, OnInit, HostListener } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as moment from 'moment';

import { BannerEditComponent } from './banner-edit/banner-edit.component';
import { CompanyService } from '../company/company.service';
import { environment } from '../../../environments/environment';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { BannerService } from './banner.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../common/components/confirm-dialog/confirm-dialog.component';

// 🔧 ADD INTERFACE FOR DIRTY CHECK (like shop-edit)
export interface CanComponentDeactivate {
  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean;
  isDirty?(): boolean;
}

@Component({
  selector: 'app-banner-settings',
  templateUrl: './banner-settings.component.html',
  styleUrls: ['./banner-settings.component.scss'],
  standalone: false
})
export class BannerSettingsComponent implements OnInit, CanComponentDeactivate {
  bannerList: any[] = [];
  originalBannerList: any[] = [];
  selectedBanner: number | null = null;
  isFormDirty = false;
  uploadFilePath = environment.assetsUrl;
  title: string = "Banner";

  constructor(
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private bannerService: BannerService,
    private snackBarService: SnackBarService
  ) {}

  // 🔧 ADD REQUIRED METHODS FOR FORM DEACTIVATE GUARD (like shop-edit)
  
  // Method used by FormDeactivateGuard (same as shop-edit)
  isDirty(): boolean {
    return this.isFormDirty;
  }

  // Standard CanDeactivate method (additional safety)
  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.isFormDirty) {
      // Create dialog data using your existing ConfirmDialogModel
      const dialogData = new ConfirmDialogModel(
        'Unsaved Changes...',
        'You have unsaved banner order changes. Are you sure you want to leave this page? All unsaved changes will be lost.'
      );
      
      // Customize button text
      dialogData.yesButtonCaption = 'Leave Page';
      dialogData.noButtonCaption = 'Stay Here';
      
      // Open your existing ConfirmDialogComponent
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '450px',
        disableClose: true,
        data: dialogData
      });

      // Return observable that emits true/false based on user choice
      return dialogRef.afterClosed().pipe(
        map(result => {
          if (result) {
            this.saveBanners()
            console.log('User chose to leave page - discarding changes');
            // Optionally reset dirty flag since user is leaving
            this.isFormDirty = false;
            return true; // Allow navigation
          } else {
            // User clicked "Stay Here" (No) or closed dialog
            console.log('User chose to stay on page');
            return false; // Prevent navigation
          }
        })
      );
    }
    
    // No dirty changes, allow navigation
    return true;
  }

  // 🔧 BROWSER REFRESH/CLOSE PROTECTION (like shop-edit)
  @HostListener('window:beforeunload', ['$event'])
  unloadHandler(event: BeforeUnloadEvent): void {
    if (this.isFormDirty) {
      event.preventDefault();
      event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
    }
  }

  ngOnInit(): void {
    this.loadData();
  }

  // Exact same loadData method as company component
  loadData(): void {
    const bannerRequest = this.bannerService.loadBanners().subscribe({
        next: (response: any) => {
            // console.log('Banner API Response:', response);
            
            if (response.status === 'success' || response.successCode === 0) {
                let bannerData = response.data;
                
                if (!Array.isArray(bannerData)) {
                    bannerData = response.data?.banners || response.data?.list || response.banners || [];
                }
                
                // console.log('Banner data found:', bannerData);
                
                // Transform API data - ALWAYS use environment path + filename
                this.bannerList = bannerData.map((banner: any) => ({
                    id: parseInt(banner.id),
                    name: this.getDisplayNameFromFileName(banner.file_name),
                    // 🔧 ALWAYS USE environment.assetsUrl + 'images/banner/' + file_name
                    imageUrl: this.uploadFilePath + 'images/banner/' + banner.file_name,
                    displayOrder: parseInt(banner.display_order),
                    fromDate: banner.period_from ? new Date(banner.period_from) : null,
                    toDate: banner.period_to ? new Date(banner.period_to) : null,
                    fileName: banner.file_name,
                    isActive: true
                }));
                
                // Sort by display order
                this.bannerList.sort((a, b) => a.displayOrder - b.displayOrder);
                
                // Store original for comparison
                this.originalBannerList = JSON.parse(JSON.stringify(this.bannerList));
                
                // console.log('Banners loaded:', this.bannerList);
            } else {
                // console.error('Failed to load banners:', response.message);
                this.bannerList = [];
                this.originalBannerList = [];
            }
        },
        error: (error) => {
            // console.error('Error loading banners:', error);
            this.bannerList = [];
            this.originalBannerList = [];
        }
    });
  }

  // Exact same addNewBanner method as company component
  addNewBanner(): void {
    const dialogRef = this.dialog.open(BannerEditComponent, {
        width: '700px',
        maxWidth: '90vw',
        disableClose: true,
        data: {
            banner: {
                name: '',
                imageUrl: '',
                fromDate: null,
                toDate: null,
                fileName: ''
            },
            index: -1,
            isNew: true
        }
    });

    dialogRef.afterClosed().subscribe(result => {
        
        
        if (result?.action === 'save') {
            
            // Check if a new file was selected
            if (result.data.file && result.data.file instanceof File) {
                
                
                // Convert file to base64 for JSON format
                const reader = new FileReader();
                reader.onload = () => {
                    // Create JSON structure with base64 image
                    const bannerData = {
                        display_order: this.bannerList.length + 1,
                        period_from: result.data.fromDate ? moment(result.data.fromDate).format('YYYY-MM-DD') : null,
                        period_to: result.data.toDate ? moment(result.data.toDate).format('YYYY-MM-DD') : null,
                        image_base64: reader.result,
                        file_name: result.data.file.name,
                        image_name: result.data.file.name,
                        image_type: result.data.file.type,
                        image_size: result.data.file.size
                    };
                    
                    
                    
                    // Call API with JSON data
                    this.bannerService.addBanner(bannerData).subscribe({
                        next: (response: any) => {
                           
                            
                            if (response.status === 'success') {
                                // 🔧 USE RESPONSE DATA TO ADD BANNER TO LIST INSTEAD OF CALLING loadData()
                                this.handleAddResponse(response, result.data, bannerData);
                            } else {
                                this.snackBarService.openSnackBar({
                                    type: 'error',
                                    title: 'Failed',
                                    message: response.message || 'Failed to add banner'
                                });
                            }
                        },
                        error: (error) => {
                            
                            this.snackBarService.openSnackBar({
                                type: 'error',
                                title: 'Failed',
                                message: error?.error?.messages?.error || 'Failed to add banner. Connection to server failed.'
                            });
                        }
                    });
                };
                
                reader.readAsDataURL(result.data.file);
            } else {
               
                
                // Handle case where no file is selected (if your API allows this)
                this.snackBarService.openSnackBar({
                    type: 'error',
                    title: 'Warning',
                    message: 'Please select an image file for the banner'
                });
            }
        }
    });
  }

  // Exact same editBanner method as company component
  editBanner(index: number): void {
    const banner = this.bannerList[index];
   
    
    // Get fresh banner data from API
    this.bannerService.getBanner(banner.id).subscribe({
        next: (response: any) => {
           
            
            if (response.status === 'success') {
                // Prepare data for dialog - ALWAYS use environment path + filename
                const bannerData = {
                    id: parseInt(response.data.id),
                    name: this.getDisplayNameFromFileName(response.data.file_name),
                    // 🔧 ALWAYS USE environment.assetsUrl + 'images/banner/' + file_name
                    imageUrl: this.uploadFilePath + 'images/banner/' + response.data.file_name,
                    displayOrder: parseInt(response.data.display_order),
                    fromDate: response.data.period_from || null,
                    toDate: response.data.period_to || null,
                    fileName: response.data.file_name,
                    isActive: true
                };
                
                
                
                const dialogRef = this.dialog.open(BannerEditComponent, {
                    width: '700px',
                    maxWidth: '90vw',
                    disableClose: true,
                    data: {
                        banner: bannerData,
                        index: index,
                        isNew: false
                    }
                });

                dialogRef.afterClosed().subscribe(result => {
                    
                    
                    if (result?.action === 'save') {
                        // Check if a new file was selected
                        if (result.data.file && result.data.file instanceof File) {
                           
                            
                            // Convert file to base64 for JSON format
                            const reader = new FileReader();
                            reader.onload = () => {
                              console.log(result.data.fromDate)
                                // Prepare JSON data with base64 image
                                const updateData = {
                                    id: parseInt(banner.id.toString()),
                                    file_name: result.data.file.name,
                                    display_order: parseInt(response.data.display_order.toString()),
                                    period_from: result.data.fromDate ? moment(result.data.fromDate).format('YYYY-MM-DD') : null,
                                    period_to: result.data.toDate ? moment(result.data.toDate).format('YYYY-MM-DD') : null,
                                    image_base64: reader.result,
                                    image_name: result.data.file.name,
                                    image_type: result.data.file.type,
                                    image_size: result.data.file.size
                                };
                                
                              
                                
                                this.bannerService.updateBanner(banner.id, updateData).subscribe({
                                    next: (updateResponse: any) => {
                                        this.handleUpdateResponse(updateResponse, index, result.data, updateData);
                                    },
                                    error: (error) => {
                                        this.handleUpdateError(error);
                                    }
                                });
                            };
                            
                            reader.readAsDataURL(result.data.file);
                        } else {
                           
                            // No file upload, just text data - KEEP ORIGINAL file_name
                            const updateData = {
                                id: parseInt(banner.id.toString()),
                                file_name: response.data.file_name,
                                display_order: parseInt(response.data.display_order.toString()),
                                period_from: result.data.fromDate ? moment(result.data.fromDate).format('YYYY-MM-DD') : null,
                                period_to: result.data.toDate ? moment(result.data.toDate).format('YYYY-MM-DD') : null
                            };
                            
                           
                            
                            this.bannerService.updateBanner(banner.id, updateData).subscribe({
                                next: (updateResponse: any) => {
                                    this.handleUpdateResponse(updateResponse, index, result.data, updateData);
                                },
                                error: (error) => {
                                    this.handleUpdateError(error);
                                }
                            });
                        }
                    }
                });
            } else {
               
                this.snackBarService.openSnackBar({
                    type: 'error',
                    title: 'Failed',
                    message: response.message || 'Failed to get banner data'
                });
            }
        },
        error: (error) => {
           
            this.snackBarService.openSnackBar({
                type: 'error',
                title: 'Failed',
                message: error?.error?.messages?.error ||'Failed to get banner data. Connection to server failed.'
            });
        }
    });
  }

  // Exact same deleteBanner method as company component
  deleteBanner(index: number): void {
    const banner = this.bannerList[index];
    
    // 🔧 SIMPLE USAGE WITH IMAGE PREVIEW
    const dialogData = new ConfirmDialogModel(
        'Delete Banner',
        `Are you sure you want to delete? This action cannot be undone.`
    ).withImage(banner.imageUrl, banner.name); // 🔧 CHAIN METHOD CALL
    
    dialogData.yesButtonCaption = 'Delete';
    dialogData.noButtonCaption = 'Cancel';
    
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '450px', // Slightly larger for image
        disableClose: true,
        data: dialogData
    });

    dialogRef.afterClosed().subscribe(confirmed => {
        if (confirmed) {
            
            
            this.bannerService.deleteBanner(banner.id).subscribe({
                next: (response: any) => {
                   
                    
                    if (response.status === 'success') {
                        // Remove from local array
                        this.bannerList.splice(index, 1);
                        
                        // Update display order for remaining banners
                        this.updateDisplayOrder();
                        
                        // Update original list for comparison
                        this.originalBannerList = JSON.parse(JSON.stringify(this.bannerList));
                        
                        // Clear selection if deleted banner was selected
                        if (this.selectedBanner === index) {
                            this.selectedBanner = null;
                        } else if (this.selectedBanner !== null && this.selectedBanner > index) {
                            this.selectedBanner--;
                        }
                        
                        // Mark as dirty
                        this.isFormDirty = true;
                        
                      
                        
                        this.snackBarService.openSnackBar({
                            type: 'success',
                            title: 'Success',
                            message: 'Banner deleted successfully'
                        });
                    } else {
                        // console.error('Failed to delete banner:', response.message);
                        this.snackBarService.openSnackBar({
                            type: 'error',
                            title: 'Failed',
                            message: response.message || 'Failed to delete banner'
                        });
                    }
                },
                error: (error) => {
                    // console.error('Error deleting banner:', error);
                    this.snackBarService.openSnackBar({
                        type: 'error',
                        title: 'Failed',
                        message: 'Failed to delete banner. Connection to server failed.'
                    });
                }
            });
        } else {
           
        }
    });
  }

  // Exact same onBannerDrop method as company component
  onBannerDrop(event: CdkDragDrop<any[]>): void {
    
    if (event.previousIndex !== event.currentIndex) {
        // Move the item in the array
        moveItemInArray(this.bannerList, event.previousIndex, event.currentIndex);
        
        
        // Update display order based on new array positions
        this.updateDisplayOrder();
        
        // 🔧 MARK AS DIRTY IMMEDIATELY (like shop-edit)
        this.isFormDirty = true;
        
        // Update selected banner index if it was moved
        if (this.selectedBanner === event.previousIndex) {
            this.selectedBanner = event.currentIndex;
        } else if (this.selectedBanner !== null) {
            if (event.previousIndex < event.currentIndex) {
                // Moving down
                if (this.selectedBanner > event.previousIndex && this.selectedBanner <= event.currentIndex) {
                    this.selectedBanner--;
                }
            } else {
                // Moving up
                if (this.selectedBanner >= event.currentIndex && this.selectedBanner < event.previousIndex) {
                    this.selectedBanner++;
                }
            }
        }
        
        // Prepare data for API call
        const orderData = this.bannerList.map((banner, index) => ({
            id: parseInt(banner.id.toString()),
            display_order: index + 1
        }));
        
        // console.log('Sending order data to API:', orderData);
        
        // Call API to update banner order
        // this.bannerService.updateBannerOrder(orderData).subscribe({
        //     next: (response: any) => {
        //         if (response.status === 'success') {
        //             console.log('Banner order updated successfully on server');
                    
        //             // Mark form as dirty since order changed
        //             this.isFormDirty = true;
                    
        //             console.log('New banner order:', this.bannerList.map(b => ({ name: b.name, displayOrder: b.displayOrder })));
                    
        //             // Show success feedback to user
        //             this.snackBarService.openSnackBar({
        //                 type: 'success',
        //                 title: 'Reordered',
        //                 message: 'Banner order updated successfully'
        //             });
        //         } else {
        //             console.error('Failed to update banner order:', response.message);
                    
        //             // Revert the changes if API call failed
        //             this.revertBannerOrder(event);
                    
        //             this.snackBarService.openSnackBar({
        //                 type: 'error',
        //                 title: 'Failed',
        //                 message: response.message || 'Failed to update banner order'
        //             });
        //         }
        //     },
        //     error: (error) => {
        //         console.error('Error updating banner order:', error);
                
        //         // Revert the changes if API call failed
        //         this.revertBannerOrder(event);
                
        //         this.snackBarService.openSnackBar({
        //             type: 'error',
        //             title: 'Failed',
        //             message: 'Failed to update banner order. Connection to server failed.'
        //         });
        //     }
        // });
    } else {
        // console.log('No position change - same index');
    }
  }

  // Exact same saveBanners method as company component
  saveBanners(): void {
    if (!this.isFormDirty) {
      this.snackBarService.openSnackBar({
        type: 'success', // ✅ CORRECT: Info type
        title: 'No Changes',
        message: 'No changes to save'
      });
      return;
    }

    // Prepare banner data for saving according to API format
    const bannerSaveData = this.bannerList.map(banner => ({
        id: parseInt(banner.id.toString()),
        display_order: parseInt(banner.displayOrder.toString())
    }));

    // console.log('Saving banner data:', bannerSaveData);
    
    // Call your API service to save banners
    this.bannerService.updateBannerOrder(bannerSaveData).subscribe({
        next: (response: any) => {
            if (response.status === 'success' || response.successCode === 0) {
                // ✅ CORRECT: Reset dirty after successful save
                this.isFormDirty = false;
                
                // Update original banner list after successful save
                this.originalBannerList = JSON.parse(JSON.stringify(this.bannerList));
                
                this.snackBarService.openSnackBar({
                    type: 'success',
                    title: 'Success',
                    message: 'Banner order saved successfully'
                });
            }
        }
    });
  }

  // Exact same discardChanges method as company component
  discardChanges(): void {
    if (!this.isFormDirty) {
        this.snackBarService.openSnackBar({
            type: 'success', // ✅ FIXED: Changed from 'success' to 'info'
            title: 'No Changes',
            message: 'No changes to discard'
        });
        return;
    }

    // 🔧 REPLACE confirm() WITH YOUR EXISTING ConfirmDialogComponent
    const dialogData = new ConfirmDialogModel(
        'Discard Changes',
        'Are you sure you want to discard all unsaved changes? Your banner order changes will be lost permanently.'
    );
    
    dialogData.yesButtonCaption = 'Discard Changes';
    dialogData.noButtonCaption = 'Keep Changes';
    
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '450px',
        disableClose: true,
        data: dialogData
    });

    dialogRef.afterClosed().subscribe(confirmed => {
        if (confirmed) {
            // User clicked "Discard Changes"
            console.log('User chose to discard changes');
            
            // Restore original banner list
            this.bannerList = JSON.parse(JSON.stringify(this.originalBannerList));
            
            // ✅ CORRECT: Reset dirty after discard
            this.isFormDirty = false;
            this.selectedBanner = null;
            
            this.snackBarService.openSnackBar({
                type: 'success',
                title: 'Discarded',
                message: 'Banner changes have been discarded'
            });
        } else {
            // User clicked "Keep Changes" or closed dialog
            console.log('User chose to keep changes');
            // Do nothing - stay on page with changes
        }
    });
  }

  // Exact same selectBanner method as company component
  selectBanner(index: number): void {
    this.selectedBanner = index;
    // console.log('Selected banner:', index);
  }

  // Exact same trackByBannerId method as company component
  trackByBannerId(index: number, banner: any): number {
    return banner.id;
  }

  // Exact same helper methods as company component
  private getDisplayNameFromFileName(fileName: string): string {
    if (!fileName) return 'Banner';
    
    // Remove file extension
    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
    
    // Replace underscores with spaces and capitalize words
    return nameWithoutExt.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  private updateDisplayOrder(): void {
    // console.log('Updating display orders...');
    this.bannerList.forEach((banner, index) => {
        const newOrder = index + 1;
        // console.log(`Banner ${banner.id}: ${banner.displayOrder} -> ${newOrder}`);
        banner.displayOrder = newOrder;
    });
    // console.log('Display order update complete');
  }

  private revertBannerOrder(event: CdkDragDrop<any[]>): void {
    // console.log('Reverting banner order due to API failure');
    
    // Move the item back to its original position
    moveItemInArray(this.bannerList, event.currentIndex, event.previousIndex);
    
    // Restore original display orders
    this.updateDisplayOrder();
    
    // Restore selected banner index
    if (this.selectedBanner === event.currentIndex) {
        this.selectedBanner = event.previousIndex;
    }
    
    // console.log('Banner order reverted');
  }

  // 🔧 FIX handleUpdateResponse with proper logic and null checks
  private handleUpdateResponse(updateResponse: any, index: number, formData: any, updateData?: any): void {
    // console.log('Update response:', updateResponse);
    // console.log('Form data:', formData);
    // console.log('Update data sent to API:', updateData);
    
    if (updateResponse.status === 'success') {
        // Use response.data if available, otherwise fall back to updateData or formData
        const responseData = updateResponse.data || {};
        
        // 🔧 DETERMINE THE FILENAME TO USE
        let fileName: string;
        
        if (responseData.file_name) {
            fileName = responseData.file_name;
            // console.log('Using responseData.file_name:', fileName);
        } else if (updateData?.file_name) {
            fileName = updateData.file_name;
            // console.log('Using updateData.file_name:', fileName);
        } else if (formData.fileName) {
            fileName = formData.fileName;
            // console.log('Using formData.fileName:', fileName);
        } else {
            fileName = this.bannerList[index].fileName;
            // console.log('Using existing fileName:', fileName);
        }
        
        // 🔧 ALWAYS CONSTRUCT URL USING: environment.assetsUrl + 'images/banner/' + file_name
        const imageUrl = this.uploadFilePath + 'images/banner/' + fileName;
        
        // 🔧 ADD CACHE BUSTING PARAMETER
        const cacheBuster = new Date().getTime(); // Current timestamp
        const finalImageUrl = imageUrl + '?cb=' + cacheBuster;
        
        
        // 🔧 BUILD UPDATED BANNER WITH ALL REQUIRED PROPERTIES
        const updatedBanner = {
            id: this.bannerList[index].id, // Keep original ID
            name: this.getDisplayNameFromFileName(fileName),
            imageUrl: finalImageUrl, // 🔧 ALWAYS USE environment path + filename + cache busting
            displayOrder: this.bannerList[index].displayOrder, // Keep display order
            fromDate: formData.fromDate ? new Date(formData.fromDate) : null,
            toDate: formData.toDate ? new Date(formData.toDate) : null,
            fileName: fileName,
            isActive: true
        };
        
        // 🔧 UPDATE THE BANNER IN THE LIST
        this.bannerList[index] = updatedBanner;
        
        // Mark form as dirty
        this.isFormDirty = true;
        
        
        this.snackBarService.openSnackBar({
            type: 'success',
            title: 'Success',
            message: 'Banner updated successfully'
        });
    } else {
        // console.error('Failed to update banner:', updateResponse.message);
        this.snackBarService.openSnackBar({
            type: 'error',
            title: 'Failed',
            message: updateResponse.message || 'Failed to update banner'
        });
    }
  }

  private handleUpdateError(error: any): void {
    this.snackBarService.openSnackBar({
        type: 'error',
        title: 'Failed',
        message: error?.error?.messages?.error || 'Failed to update banner. Connection to server failed.'
    });
  }

  // 🔧 ADD NEW METHOD TO HANDLE ADD RESPONSE - SIMILAR TO handleUpdateResponse
  private handleAddResponse(addResponse: any, formData: any, bannerData: any): void {
   
    if (addResponse.status === 'success') {
        // Use response.data if available, otherwise fall back to bannerData
        const responseData = addResponse.data || {};
        
        // 🔧 DETERMINE THE FILENAME TO USE
        let fileName: string;
        
        if (responseData.file_name) {
            fileName = responseData.file_name;
            // console.log('Using responseData.file_name:', fileName);
        } else if (bannerData?.file_name) {
            fileName = bannerData.file_name;
            // console.log('Using bannerData.file_name:', fileName);
        } else if (formData.fileName) {
            fileName = formData.fileName;
            // console.log('Using formData.fileName:', fileName);
        } else {
            fileName = formData.file?.name || 'banner.jpg';
            // console.log('Using fallback fileName:', fileName);
        }
        
        // 🔧 ALWAYS CONSTRUCT URL USING: environment.assetsUrl + 'images/banner/' + file_name
        const imageUrl = this.uploadFilePath + 'images/banner/' + fileName;
        
        // 🔧 ADD CACHE BUSTING TO FORCE BROWSER REFRESH
        const cacheBuster = new Date().getTime();
        const finalImageUrl = imageUrl + '?cb=' + cacheBuster;
        
       
        
        // 🔧 BUILD NEW BANNER OBJECT
        const newBanner = {
            id: parseInt(responseData.id || Date.now().toString()), // Use response ID or timestamp fallback
            name: this.getDisplayNameFromFileName(fileName),
            imageUrl: finalImageUrl, // 🔧 ALWAYS USE environment path + filename + cache busting
            displayOrder: bannerData.display_order,
            fromDate: formData.fromDate ? new Date(formData.fromDate) : null,
            toDate: formData.toDate ? new Date(formData.toDate) : null,
            fileName: fileName,
            isActive: true
        };
        
        // console.log('New banner object:', newBanner);
        
        // 🔧 ADD TO BANNER LIST
        this.bannerList.push(newBanner);
        
        // 🔧 SORT BY DISPLAY ORDER
        this.bannerList.sort((a, b) => a.displayOrder - b.displayOrder);
        
        // 🔧 UPDATE ORIGINAL BANNER LIST FOR COMPARISON
        this.originalBannerList = JSON.parse(JSON.stringify(this.bannerList));
        
        // Mark form as dirty
        this.isFormDirty = true;
        
        
        this.snackBarService.openSnackBar({
            type: 'success',
            title: 'Success',
            message: 'Banner added successfully'
        });
    } else {
        // console.error('Failed to add banner:', addResponse.message);
        this.snackBarService.openSnackBar({
            type: 'error',
            title: 'Failed',
            message: addResponse.message || 'Failed to add banner'
        });
    }
}

// 🔧 ADD METHOD TO GENERATE TOOLTIP TEXT FOR BANNER PERIODS
getBannerPeriodTooltip(banner: any): string {
  if (banner.fromDate && banner.toDate) {
    return `Period: ${this.formatDate(banner.fromDate)} - ${this.formatDate(banner.toDate)}`;
  } else if (banner.fromDate) {
    return `From: ${this.formatDate(banner.fromDate)}`;
  } else if (banner.toDate) {
    return `Until: ${this.formatDate(banner.toDate)}`;
  }
  return 'Scheduled Banner';
}

// 🔧 ADD HELPER METHOD TO FORMAT DATES
private formatDate(date: Date | string | null): string {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  if (isNaN(dateObj.getTime())) return '';
  
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}
}
